/**
 * Queue List Component
 * Displays a scrollable list of queue items with selection for bulk operations
 */

import React, { useMemo, useState } from 'react';

/**
 * Format age string (e.g., "2h 30m ago")
 */
const formatAge = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return 'Just now';
};

/**
 * Get document type icon
 */
const TypeIcon = ({ type, className = 'w-4 h-4' }) => {
  switch (type) {
    case 'flight':
    case 'commercial_flight':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      );
    case 'hotel':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      );
    case 'car_rental':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8m-8 5h8m-4 5v-2m-6 2h12a2 2 0 002-2v-5a2 2 0 00-2-2h-1.5l-1-3h-7l-1 3H5a2 2 0 00-2 2v5a2 2 0 002 2z" />
        </svg>
      );
    case 'private_flight':
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      );
    default:
      return (
        <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
  }
};

const QueueList = ({
  items = [],
  currentItemId,
  loading = false,
  onSelect,
  getPriorityColor,
  getStatusColor,
  // Selection props
  selectedItems = new Set(),
  onToggleSelection,
  onSelectAll,
  onClearSelection,
  onDeleteSelected,
  isDeleting = false
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectedCount = selectedItems.size;
  const allSelected = items.length > 0 && selectedCount === items.length;
  const someSelected = selectedCount > 0 && selectedCount < items.length;

  // Group items by status for visual organization
  const groupedItems = useMemo(() => {
    const groups = {
      urgent: [],
      pending: [],
      in_progress: [],
      other: []
    };

    for (const item of items) {
      if (item.priority === 'urgent') {
        groups.urgent.push(item);
      } else if (item.status === 'pending') {
        groups.pending.push(item);
      } else if (item.status === 'in_progress') {
        groups.in_progress.push(item);
      } else {
        groups.other.push(item);
      }
    }

    return groups;
  }, [items]);

  // Handle delete with confirmation
  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    const result = await onDeleteSelected?.();
    setShowDeleteConfirm(false);

    if (result?.success) {
      // Success - selection already cleared by parent
    } else if (result?.error) {
      console.error('Delete failed:', result.error);
    }
  };

  // Render a single item
  const renderItem = (item) => {
    const isCurrent = item.id === currentItemId;
    const isSelected = selectedItems.has(item.id);

    return (
      <div
        key={item.id}
        className={`w-full text-left p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
          isCurrent ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
        }`}
      >
        <div className="flex items-start gap-2">
          {/* Selection checkbox */}
          <div className="flex-shrink-0 pt-0.5">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleSelection?.(item.id);
              }}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
          </div>

          {/* Clickable content */}
          <button
            onClick={() => onSelect(item.id)}
            className="flex-1 min-w-0 text-left"
          >
            <div className="flex items-start gap-2">
              {/* Type icon */}
              <div className="flex-shrink-0 mt-0.5 text-gray-400">
                <TypeIcon type={item.document_type} className="w-4 h-4" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Header row: priority + status */}
                <div className="flex items-center gap-1.5 mb-1">
                  <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${getPriorityColor(item.priority)}`}>
                    {item.priority?.toUpperCase()}
                  </span>
                  <span className={`px-1.5 py-0.5 text-[10px] rounded ${getStatusColor(item.status)}`}>
                    {item.status?.replace('_', ' ')}
                  </span>
                </div>

                {/* Organization */}
                <div className="text-sm font-medium text-gray-900 truncate">
                  {item.organization_name || 'Unknown Org'}
                </div>

                {/* Subject */}
                <div className="text-xs text-gray-500 truncate">
                  {item.email_subject || 'No subject'}
                </div>

                {/* Footer: carrier + age */}
                <div className="mt-1 flex items-center gap-2 text-[10px] text-gray-400">
                  {item.carrier_code && (
                    <span className="font-medium">{item.carrier_code}</span>
                  )}
                  {item.document_type && (
                    <span>{item.document_type.replace('_', ' ')}</span>
                  )}
                  <span className="ml-auto">{formatAge(item.created_at)}</span>
                </div>

                {/* Issues indicator */}
                {(item.validation_errors?.length > 0 || item.missing_fields?.length > 0) && (
                  <div className="mt-1 flex items-center gap-1 text-[10px] text-red-500">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>
                      {(item.validation_errors?.length || 0) + (item.missing_fields?.length || 0)} issues
                    </span>
                  </div>
                )}
              </div>

              {/* Current selection indicator */}
              {isCurrent && (
                <div className="flex-shrink-0">
                  <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          </button>
        </div>
      </div>
    );
  };

  // Render a section header
  const renderSectionHeader = (title, count, colorClass = 'text-gray-500') => {
    if (count === 0) return null;

    return (
      <div className={`sticky top-0 bg-gray-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider ${colorClass} border-b border-gray-200`}>
        {title} ({count})
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {/* Select all checkbox */}
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={() => {
                if (allSelected || someSelected) {
                  onClearSelection?.();
                } else {
                  onSelectAll?.();
                }
              }}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              title={allSelected ? 'Deselect all' : 'Select all'}
            />
            <span className="text-sm font-medium text-gray-700">Queue</span>
          </div>
          <span className="text-xs text-gray-500">{items.length} items</span>
        </div>

        {/* Selection actions bar */}
        {selectedCount > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-gray-600">
              {selectedCount} selected
            </span>
            <button
              onClick={onClearSelection}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
            <div className="flex-1" />
            {showDeleteConfirm ? (
              <div className="flex items-center gap-1">
                <span className="text-xs text-red-600">Delete {selectedCount}?</span>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {isDeleting ? 'Deleting...' : 'Confirm'}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleDelete}
                className="px-2 py-0.5 text-xs text-red-600 hover:text-red-800 border border-red-200 rounded hover:bg-red-50 flex items-center gap-1"
                title="Delete selected items"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <svg className="w-6 h-6 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No items in queue
          </div>
        ) : (
          <div>
            {/* Urgent items */}
            {groupedItems.urgent.length > 0 && (
              <div>
                {renderSectionHeader('Urgent', groupedItems.urgent.length, 'text-red-600 bg-red-50')}
                {groupedItems.urgent.map(renderItem)}
              </div>
            )}

            {/* Pending items */}
            {groupedItems.pending.length > 0 && (
              <div>
                {renderSectionHeader('Pending', groupedItems.pending.length, 'text-yellow-700 bg-yellow-50')}
                {groupedItems.pending.map(renderItem)}
              </div>
            )}

            {/* In Progress items */}
            {groupedItems.in_progress.length > 0 && (
              <div>
                {renderSectionHeader('In Progress', groupedItems.in_progress.length, 'text-blue-600 bg-blue-50')}
                {groupedItems.in_progress.map(renderItem)}
              </div>
            )}

            {/* Other items */}
            {groupedItems.other.length > 0 && (
              <div>
                {renderSectionHeader('Other', groupedItems.other.length)}
                {groupedItems.other.map(renderItem)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer with keyboard hint */}
      <div className="flex-shrink-0 px-3 py-2 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-400">
        <span className="flex items-center gap-1">
          <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[9px]">J</kbd>
          <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[9px]">K</kbd>
          navigate
          <span className="mx-1">|</span>
          <kbd className="px-1 py-0.5 bg-white border border-gray-200 rounded text-[9px]">Space</kbd>
          select
        </span>
      </div>
    </div>
  );
};

export default QueueList;
